import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Route-scoped navbar action portal. A layout drops one `LayoutActionsSlot` in
 * its header; any route or feature rendered inside that layout pushes controls
 * into it with `NavbarActions`. They mount when the route mounts and vanish when
 * it unmounts, so history/share style buttons only show on the pages that render
 * them, never bolted onto every route.
 *
 * No context or provider: the slot is a plain DOM node with a known id and
 * `NavbarActions` portals into it. This file is identical across every layout
 * pack (enforced by `layout-slots.sync.test.ts`), so a feature imports it from
 * the same path no matter which layout the user installed.
 */
export const NAVBAR_ACTIONS_SLOT_ID = "navbar-actions-slot";
const SLOT_CHANGED_EVENT = "layout:navbar-actions-slot-changed";

function notifySlotChange() {
  // Resolve after the commit so a replaced desktop/mobile header is in the DOM.
  queueMicrotask(() => window.dispatchEvent(new Event(SLOT_CHANGED_EVENT)));
}

/**
 * Navbar mount point for route-scoped actions. Collapses to nothing until a
 * route fills it (`empty:hidden`), so an empty slot adds no stray gap next to
 * the static navbar actions and theme toggle.
 */
export function LayoutActionsSlot() {
  return <div ref={notifySlotChange} id={NAVBAR_ACTIONS_SLOT_ID} className="flex items-center gap-2 empty:hidden" />;
}

/** Renders its children into the navbar actions slot from anywhere inside the layout. */
export function NavbarActions({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  // The slot lives in the layout header (an ancestor), so it is in the DOM by
  // the time this effect runs. Resolve it after mount rather than during render,
  // where it would not exist yet on the first paint.
  useEffect(() => {
    const updateSlot = () => setSlot(document.getElementById(NAVBAR_ACTIONS_SLOT_ID));
    updateSlot();
    window.addEventListener(SLOT_CHANGED_EVENT, updateSlot);
    return () => window.removeEventListener(SLOT_CHANGED_EVENT, updateSlot);
  }, []);

  if (!slot) return null;
  return createPortal(children, slot);
}
